/* eslint-disable react/no-danger */
import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatMoney, formatPercent } from 'utils/formatter';
import { formStyles } from 'styles/formStyles';
import moment from 'moment-timezone';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { lookupsLoadingFinishedSelector, produktgruppenByNrSelector } from 'store/lookups/selector';
import { styles } from './Rechnungsuebersicht';
import { FilterValues, LineData } from './types';
import { RechnungSummaryProps, useRechnungSummary } from './hooks/useRechnungSummary';

interface RechnungsuebersichtListProps extends FilterValues {
    printView?: boolean;
}

export const RechnungsuebersichtList: React.FC<RechnungsuebersichtListProps> = ({
    statusList,
    datumVon,
    datumBis,
    kNr,
    kundePlzVon,
    kundePlzBis,
    kundeUmsatzTsdVon,
    kundeUmsatzTsdBis,
    kundeBranchen,
    kundeErweiterteSuchen,
    kundeInteresseFuerProdukte,
    kundeBetreuer,
    kundeTelBetreuer,
    betreuer,
    telBetreuer,
    produktgruppen,
    showDetails,
    previousYear1,
    printView = false,
}) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const produktgruppenByNr = useSelector(produktgruppenByNrSelector);
    const auth = useSelector(authSelector);
    const rechnungSummaryProps = React.useMemo<RechnungSummaryProps>(
        () => ({
            statusList,
            datumVon,
            datumBis,
            kNr,
            kundePlzVon,
            kundePlzBis,
            kundeUmsatzTsdVon,
            kundeUmsatzTsdBis,
            kundeBranchen,
            kundeErweiterteSuchen,
            kundeInteresseFuerProdukte,
            kundeBetreuer,
            kundeTelBetreuer,
            betreuer,
            telBetreuer,
            produktgruppen,
            showDetails,
            previousYear1,
            enabled: true,
        }),
        [
            statusList,
            datumVon,
            datumBis,
            kNr,
            kundePlzVon,
            kundePlzBis,
            kundeUmsatzTsdVon,
            kundeUmsatzTsdBis,
            kundeBranchen,
            kundeErweiterteSuchen,
            kundeInteresseFuerProdukte,
            kundeBetreuer,
            kundeTelBetreuer,
            betreuer,
            telBetreuer,
            produktgruppen,
            showDetails,
            previousYear1,
        ],
    );

    const rechnungSummary = useRechnungSummary(rechnungSummaryProps);

    const renderHeaderRow = () => {
        const moneyClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
        );

        const percentClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
        );

        return (
            <tr>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportRNrPrintCell : formStyles.reportRNrCell,
                    )}
                >
                    RechnungNr.
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                >
                    Datum
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                    )}
                >
                    Kunde
                </td>
                <td className={classnames(percentClassName, styles.ekNetto)} />
                <td className={classnames(moneyClassName, styles.ekNetto)}>EK Netto</td>
                <td className={classnames(percentClassName, styles.vkNetto)} />
                <td className={classnames(moneyClassName, styles.vkNetto)}>VK Netto</td>
                <td className={classnames(percentClassName, styles.bMarge)} />
                <td className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}>B-Marge</td>
                <td className={classnames(percentClassName, styles.tippProv)} />
                <td className={classnames(moneyClassName, styles.tippProv)}>Tipp-Pr.</td>
                <td className={classnames(percentClassName, styles.skonto)} />
                <td className={classnames(moneyClassName, styles.skonto)}>Skonto</td>
                <td className={classnames(percentClassName, styles.fracht)} />
                <td className={classnames(moneyClassName, styles.fracht)}>Fracht</td>
                <td className={classnames(percentClassName, styles.nMarge)} />
                <td className={classnames(moneyClassName, styles.nMarge, styles.borderRight)}>N-Marge</td>
                <td className={classnames(percentClassName, styles.bAnteil)} />
                <td className={classnames(moneyClassName, styles.bAnteil)}>B-Anteil</td>
                {auth.rolle !== 'AD' && (
                    <>
                        <td className={classnames(percentClassName, styles.fAnteil)} />
                        <td className={classnames(moneyClassName, styles.fAnteil)}>F-Anteil</td>
                    </>
                )}
            </tr>
        );
    };

    const moneyClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );
    const percentClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
    );

    const renderMoney = (value: number, className: string, borderRight?: boolean) => {
        return (
            <>
                <td className={classnames(percentClassName, className)} />
                <td
                    className={classnames(moneyClassName, className, {
                        [styles.borderRight]: borderRight,
                    })}
                >
                    {formatMoney(value)}
                </td>
            </>
        );
    };

    const renderSummaryLine = (title: string, line: LineData) => {
        return (
            <tr>
                <td colSpan={3}>{title}</td>
                {renderMoney(line.ekNetto, styles.ekNetto)}
                {renderMoney(line.vkNetto, styles.vkNetto)}
                {renderMoney(line.bMarge, styles.bMarge, true)}
                {renderMoney(line.tippProv, styles.tippProv)}
                {renderMoney(line.skonto, styles.skonto)}
                {renderMoney(line.fracht, styles.fracht)}
                {renderMoney(line.nMarge, styles.nMarge, true)}
                {renderMoney(line.bAnteil, styles.bAnteil)}
                {auth.rolle !== 'AD' && renderMoney(line.fAnteil, styles.fAnteil)}
            </tr>
        );
    };

    const renderLine = (line: LineData) => {
        const recordUrl = createRecordUrl('rechnungen', line.kNr, line.rNr);

        return (
            <tr key={line.rNr}>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportRNrPrintCell : formStyles.reportRNrCell,
                    )}
                >
                    {printView ? line.rNr : <OpenLink link={recordUrl} label={line.rNr} />}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                >
                    {moment(line.datum).format('DD.MM.YYYY')}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                    )}
                >
                    {line.kName}
                </td>
                {renderMoney(line.ekNetto, styles.ekNetto)}
                {renderMoney(line.vkNetto, styles.vkNetto)}
                {renderMoney(line.bMarge, styles.bMarge, true)}
                {renderMoney(line.tippProv, styles.tippProv)}
                {renderMoney(line.skonto, styles.skonto)}
                {renderMoney(line.fracht, styles.fracht)}
                {renderMoney(line.nMarge, styles.nMarge, true)}
                {renderMoney(line.bAnteil, styles.bAnteil)}
                {auth.rolle !== 'AD' && renderMoney(line.fAnteil, styles.fAnteil)}
            </tr>
        );
    };

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            {!printView && <table className={formStyles.reportFixedHeaderNoBgColor}>{renderHeaderRow()}</table>}
            <h1>Rechnungsübersicht</h1>
            <table className={formStyles.reportTable}>
                <thead>{renderHeaderRow()}</thead>
                <tbody>
                    {(rechnungSummary.rechnungLineDataList || []).map((line) => renderLine(line))}

                    <tr>
                        <td colSpan={21} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                    </tr>

                    {renderSummaryLine(
                        'Gesamt (' + (rechnungSummary ? rechnungSummary.count : '...') + ')',
                        rechnungSummary.total,
                    )}
                    <tr>
                        <td colSpan={3}>%-Anteil</td>
                        <td className={classnames(percentClassName, styles.ekNetto)} />
                        <td className={classnames(percentClassName, styles.ekNetto)} />
                        <td className={classnames(percentClassName, styles.vkNetto)} />
                        <td className={classnames(percentClassName, styles.vkNetto)} />

                        <td className={classnames(percentClassName, styles.bMarge)} />
                        <td className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}>
                            {formatPercent(rechnungSummary.total.bMarge / (rechnungSummary.total.vkNetto / 100), 2)}
                        </td>
                        <td className={classnames(percentClassName, styles.tippProv)} />
                        <td className={classnames(moneyClassName, styles.tippProv)}>
                            {formatPercent(rechnungSummary.total.tippProv / (rechnungSummary.total.vkNetto / 100), 2)}
                        </td>
                        <td className={classnames(percentClassName, styles.skonto)} />
                        <td className={classnames(moneyClassName, styles.skonto)}>
                            {formatPercent(rechnungSummary.total.skonto / (rechnungSummary.total.vkNetto / 100), 2)}
                        </td>
                        <td className={classnames(percentClassName, styles.fracht)} />
                        <td className={classnames(moneyClassName, styles.fracht)}>
                            {formatPercent(rechnungSummary.total.fracht / (rechnungSummary.total.vkNetto / 100), 2)}
                        </td>
                        <td className={classnames(percentClassName, styles.nMarge)} />
                        <td className={classnames(moneyClassName, styles.nMarge, styles.borderRight)}>
                            {formatPercent(rechnungSummary.total.nMarge / (rechnungSummary.total.vkNetto / 100), 2)}
                        </td>
                        <td className={classnames(percentClassName, styles.bAnteil)} />
                        <td className={classnames(moneyClassName, styles.bAnteil)}>
                            {formatPercent(rechnungSummary.total.bAnteil / (rechnungSummary.total.vkNetto / 100), 2)}
                        </td>
                        {auth.rolle !== 'AD' && (
                            <>
                                <td className={classnames(percentClassName, styles.fAnteil)} />
                                <td className={classnames(moneyClassName, styles.fAnteil)}>
                                    {formatPercent(
                                        rechnungSummary.total.fAnteil / (rechnungSummary.total.vkNetto / 100),
                                        2,
                                    )}
                                </td>
                            </>
                        )}
                    </tr>
                    <tr>
                        <td colSpan={3}>Betreuer ({rechnungSummary.total.betrUmCount})</td>
                        <td className={classnames(percentClassName, styles.ekNetto)} />
                        <td className={classnames(percentClassName, styles.ekNetto)} />
                        {renderMoney(rechnungSummary.total.betrUm, styles.vkNetto)}
                        <td className={classnames(percentClassName, styles.bMarge)} />
                        <td className={classnames(percentClassName, styles.bMarge, styles.borderRight)}>
                            {rechnungSummary.total.vkNetto
                                ? formatPercent(rechnungSummary.total.betrUm / (rechnungSummary.total.vkNetto / 100), 2)
                                : ''}
                        </td>
                        <td className={classnames(percentClassName, styles.tippProv)} />
                        <td className={classnames(percentClassName, styles.tippProv)} />
                        <td className={classnames(percentClassName, styles.skonto)} />
                        <td className={classnames(percentClassName, styles.skonto)} />
                        <td className={classnames(percentClassName, styles.fracht)} />
                        <td className={classnames(percentClassName, styles.fracht)} />
                        <td className={classnames(percentClassName, styles.nMarge)} />
                        <td className={classnames(percentClassName, styles.nMarge, styles.borderRight)} />
                        <td className={classnames(percentClassName, styles.bAnteil)} />
                        <td className={classnames(percentClassName, styles.bAnteil)} />
                        {auth.rolle !== 'AD' && (
                            <>
                                <td className={classnames(percentClassName, styles.fAnteil)} />
                                <td className={classnames(percentClassName, styles.fAnteil)} />
                            </>
                        )}
                    </tr>

                    <tr>
                        <td colSpan={21} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                    </tr>

                    {Object.values(rechnungSummary.betreuerTotal)
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((betreuerTotal) => {
                            return renderSummaryLine(betreuerTotal.name || '?', betreuerTotal);
                        })}
                    <tr>
                        <td colSpan={21} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                    </tr>

                    <tr>
                        <td colSpan={3}>Gesamt </td>
                        <td className={classnames(percentClassName, styles.ekNetto)} />
                        <td className={classnames(percentClassName, styles.ekNetto)} />
                        <td className={classnames(percentClassName, styles.vkNetto)} />
                        <td className={classnames(percentClassName, styles.vkNetto)} />
                        <td className={classnames(percentClassName, styles.bMarge)} />
                        <td className={classnames(percentClassName, styles.bMarge, styles.borderRight)} />
                        <td className={classnames(percentClassName, styles.tippProv)} />
                        <td className={classnames(percentClassName, styles.tippProv)} />
                        <td className={classnames(percentClassName, styles.skonto)} />
                        <td className={classnames(percentClassName, styles.skonto)} />
                        <td className={classnames(percentClassName, styles.fracht)} />
                        <td className={classnames(percentClassName, styles.fracht)} />
                        <td className={classnames(percentClassName, styles.nMarge)} />
                        <td className={classnames(percentClassName, styles.nMarge, styles.borderRight)} />
                        <td className={classnames(percentClassName, styles.bAnteil)} />
                        <td className={classnames(percentClassName, styles.bAnteil)} />
                        {auth.rolle !== 'AD' && renderMoney(rechnungSummary.fAnteilTotal, styles.fAnteilTotal)}
                    </tr>
                    <tr>
                        <td colSpan={21} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                    </tr>
                </tbody>
            </table>
            <div className={styles.produktgruppen}>
                Produktgruppen:{' '}
                {Object.keys(rechnungSummary?.usedProduktgruppen || {})
                    .map(
                        (produktGruppeNr) =>
                            produktgruppenByNr[produktGruppeNr]?.text +
                            ' (' +
                            rechnungSummary?.usedProduktgruppen?.[produktGruppeNr] +
                            ')',
                    )
                    .join(', ')}
            </div>
        </div>
    );
};
